import axios from 'axios'
import React, {useEffect, useMemo, useState} from 'react'
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js'
import {Chart} from 'react-chartjs-2'
import {KTCard, KTCardBody, KTIcon} from '../../../../../_websoft/helpers'
import {prosjecnaDebljinaPanelaIzvjestaj} from '../../../../api/izvjestaj'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import TitleComponent from '../../../../components/TitleComponent/TitleComponent'

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend
)

type MonthValue = number | null

type MonthRow = {
  mjesec: number
  tekuca_godina: MonthValue
  prosla_godina: MonthValue
}

type IsolationRow = {
  izolacija_id: number
  izolacija_naziv: string
  mjeseci: MonthRow[]
}

type AverageThicknessData = {
  tekuca_godina: number
  prosla_godina: number
  jedinica_mjere: string
  podaci: IsolationRow[]
  sve_izolacije?: MonthRow[] | null
}

type ReportEnvelope = {
  error?: number
  success?: boolean
  message?: string
  data?: AverageThicknessData | null
}

type NormalizedMonth = {
  mjesec: number
  tekuca_godina: MonthValue
  prosla_godina: MonthValue
}

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'Mart',
  'April',
  'Maj',
  'Juni',
  'Juli',
  'August',
  'Septembar',
  'Oktobar',
  'Novembar',
  'Decembar',
]

const ALL_ISOLATIONS_TAB = 'sve-izolacije'
const ALL_ISOLATIONS_DESCRIPTION =
  'Prosječna debljina svih završenih panela po mjesecu, ponderisana količinom, za sve izolacije zajedno.'

const chartColors = {
  current: '#1b84ff',
  previous: '#50cd89',
}

const getTabClassName = (isActive: boolean) =>
  `nav-link fw-bold text-nowrap rounded px-3 px-sm-5 py-2 py-sm-3 ${
    isActive ? 'active bg-primary text-white' : 'bg-light text-gray-700'
  }`

const formatValue = (value: MonthValue, unit: string) => {
  if (value === null) {
    return '—'
  }

  return `${value} ${unit}`
}

const calculateAverage = (
  months: NormalizedMonth[],
  getValue: (month: NormalizedMonth) => MonthValue
) => {
  const values = months.map(getValue).filter((value): value is number => value !== null)

  if (values.length === 0) {
    return null
  }

  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
}

const formatAverage = (value: number | null, unit: string) =>
  value === null ? '—' : `${value} ${unit}`

const getMonths = (months: MonthRow[] | null | undefined): NormalizedMonth[] =>
  Array.from({length: 12}, (_, index) => {
    const monthNumber = index + 1
    const month = months?.find((item) => item.mjesec === monthNumber)

    return {
      mjesec: monthNumber,
      tekuca_godina: month?.tekuca_godina ?? null,
      prosla_godina: month?.prosla_godina ?? null,
    }
  })

const normalizeResponse = (payload: ReportEnvelope | null | undefined) => {
  const reportData = payload?.data
  const podaci = reportData?.podaci
  const sveIzolacije = reportData?.sve_izolacije

  return {
    tekuca_godina: reportData?.tekuca_godina ?? null,
    prosla_godina: reportData?.prosla_godina ?? null,
    jedinica_mjere: reportData?.jedinica_mjere?.trim() || 'mm',
    podaci: Array.isArray(podaci) ? podaci : [],
    sve_izolacije: Array.isArray(sveIzolacije) ? sveIzolacije : [],
  }
}

const getChartOptions = (unit: string): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = context.parsed.y

          return `${context.dataset.label ?? ''}: ${value === null ? '—' : `${value} ${unit}`}`
        },
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Mjesec',
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: `Debljina (${unit})`,
      },
    },
  },
})

const ProsjecnaDebljinaPanelaSadrzaj: React.FC = () => {
  const [report, setReport] = useState<ReturnType<typeof normalizeResponse> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTabKey, setActiveTabKey] = useState(ALL_ISOLATIONS_TAB)

  useEffect(() => {
    const controller = new AbortController()
    let isActive = true

    const loadReport = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await prosjecnaDebljinaPanelaIzvjestaj(controller.signal)
        const payload = response.data as ReportEnvelope

        if (
          payload?.success === false ||
          (typeof payload?.error === 'number' && payload.error !== 0)
        ) {
          throw new Error(payload.message || 'Greska prilikom ucitavanja izvjestaja.')
        }

        if (isActive) {
          setReport(normalizeResponse(payload))
        }
      } catch (requestError: any) {
        if (!isActive || axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') {
          return
        }

        setReport(null)
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            'Greska prilikom ucitavanja izvjestaja.'
        )
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }

    loadReport()

    return () => {
      isActive = false
      controller.abort()
    }
  }, [])

  const isolations = useMemo(
    () =>
      (report?.podaci ?? []).map((isolation, index) => {
        const months = getMonths(isolation.mjeseci)

        return {
          ...isolation,
          key: `${isolation.izolacija_id ?? isolation.izolacija_naziv ?? 'izolacija'}-${index}`,
          name: isolation.izolacija_naziv?.trim() || 'Nepoznata izolacija',
          months,
          hasMonthlyData: months.some(
            (month) => month.tekuca_godina !== null || month.prosla_godina !== null
          ),
        }
      }),
    [report]
  )

  const allIsolationsMonths = useMemo(() => getMonths(report?.sve_izolacije), [report])
  const allIsolationsHaveData = allIsolationsMonths.some(
    (month) => month.tekuca_godina !== null || month.prosla_godina !== null
  )
  const hasData = isolations.length > 0 || (report?.sve_izolacije.length ?? 0) > 0
  const unit = report?.jedinica_mjere || 'mm'
  const chartOptions = useMemo(() => getChartOptions(unit), [unit])
  const selectedTab = useMemo(
    () => {
      if (activeTabKey === ALL_ISOLATIONS_TAB) {
        return {
          key: ALL_ISOLATIONS_TAB,
          name: 'Sve izolacije',
          description: ALL_ISOLATIONS_DESCRIPTION,
          months: allIsolationsMonths,
          hasMonthlyData: allIsolationsHaveData,
        }
      }

      const isolation = isolations.find((item) => item.key === activeTabKey)

      return isolation
        ? {...isolation, description: ''}
        : {
            key: ALL_ISOLATIONS_TAB,
            name: 'Sve izolacije',
            description: ALL_ISOLATIONS_DESCRIPTION,
            months: allIsolationsMonths,
            hasMonthlyData: allIsolationsHaveData,
          }
    },
    [activeTabKey, allIsolationsHaveData, allIsolationsMonths, isolations]
  )
  const chartData = useMemo<ChartData<'line'> | null>(() => {
    if (!selectedTab || !report) {
      return null
    }

    return {
      labels: MONTH_NAMES,
      datasets: [
        {
          label: String(report.tekuca_godina ?? 'Tekuca godina'),
          data: selectedTab.months.map((month) => month.tekuca_godina),
          borderColor: chartColors.current,
          backgroundColor: chartColors.current,
          pointBackgroundColor: chartColors.current,
          pointRadius: 4,
          tension: 0.25,
          spanGaps: false,
        },
        {
          label: String(report.prosla_godina ?? 'Prosla godina'),
          data: selectedTab.months.map((month) => month.prosla_godina),
          borderColor: chartColors.previous,
          backgroundColor: chartColors.previous,
          pointBackgroundColor: chartColors.previous,
          pointRadius: 4,
          tension: 0.25,
          spanGaps: false,
        },
      ],
    }
  }, [report, selectedTab])
  const selectedAverages = useMemo(
    () => ({
      current: calculateAverage(selectedTab.months, (month) => month.tekuca_godina),
      previous: calculateAverage(selectedTab.months, (month) => month.prosla_godina),
    }),
    [selectedTab]
  )

  return (
    <>
      {error && (
        <div
          className='alert alert-danger d-flex align-items-start align-items-sm-center p-4 p-sm-5 mb-6 mb-sm-8'
          role='alert'
        >
          <KTIcon iconName='information-2' className='fs-2 me-3 mt-1 mt-sm-0' />
          <span className='fw-bold'>{error}</span>
        </div>
      )}

      {loading && <LoadingComponent />}

      {!loading && !error && !hasData && (
        <div
          className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 p-sm-6'
        >
          <div className='fw-bold text-gray-700'>Nema podataka za prikaz.</div>
        </div>
      )}

      {!loading && !error && hasData && report && (
        <>
          <div
            className='notice d-flex flex-column flex-sm-row align-items-start align-items-sm-center bg-light-info rounded border-info border border-dashed p-4 p-sm-5 mb-6 mb-sm-8'
          >
            <KTIcon
              iconName='information-2'
              className='fs-2 text-info me-sm-3 mb-2 mb-sm-0'
            />
            <div className='fw-bold text-gray-700'>
              Poredjenje prosjecne debljine za {report.tekuca_godina ?? '-'} i{' '}
              {report.prosla_godina ?? '-'} godine.
              <span className='d-block d-sm-inline ms-sm-2 mt-1 mt-sm-0 text-muted'>
                Jedinica mjere: {unit}
              </span>
            </div>
          </div>

          <ul
            className='nav nav-tabs nav-line-tabs nav-line-tabs-2x flex-nowrap overflow-auto pb-2 mb-6 mb-md-8 fs-6'
            role='tablist'
          >
            <li className='nav-item me-2'>
              <button
                type='button'
                className={getTabClassName(activeTabKey === ALL_ISOLATIONS_TAB)}
                onClick={() => setActiveTabKey(ALL_ISOLATIONS_TAB)}
                aria-selected={activeTabKey === ALL_ISOLATIONS_TAB}
                role='tab'
              >
                Sve izolacije
              </button>
            </li>
            {isolations.map((isolation) => (
              <li className='nav-item me-2' key={isolation.key}>
                <button
                  type='button'
                  className={getTabClassName(activeTabKey === isolation.key)}
                  onClick={() => setActiveTabKey(isolation.key)}
                  aria-selected={activeTabKey === isolation.key}
                  role='tab'
                >
                  {isolation.name}
                </button>
              </li>
            ))}
          </ul>

          {selectedTab && chartData && (
            <div className='row g-4 g-md-6'>
              <div className='col-12'>
                <div className='card border border-gray-300'>
                  <div className='card-header px-4 px-sm-6 py-4'>
                    <div className='card-title flex-column align-items-start my-0'>
                      <h3 className='fw-bolder mb-0'>{selectedTab.name}</h3>
                      {selectedTab.description && (
                        <div className='text-muted fw-semibold fs-7 mt-2'>
                          {selectedTab.description}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='card-body p-3 p-sm-5 p-lg-9'>
                    {selectedTab.hasMonthlyData ? (
                      <div style={{height: 'clamp(260px, 70vw, 350px)'}}>
                        <Chart
                          key={selectedTab.key}
                          type='line'
                          data={chartData}
                          options={chartOptions}
                        />
                      </div>
                    ) : (
                      <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 p-sm-6'>
                        <div className='fw-bold text-gray-700'>
                          Nema podataka za odabrane godine
                        </div>
                      </div>
                    )}

                    <div className='table-responsive mt-6 mt-md-8'>
                      <table
                        className='table table-row-dashed table-row-gray-300 align-middle gy-3 gy-md-4 mb-0'
                        style={{minWidth: 520}}
                      >
                        <thead>
                          <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                            <th className='text-white text-nowrap ps-3 ps-sm-4'>Mjesec</th>
                            <th className='text-white text-nowrap text-end'>
                              <span className='d-none d-sm-inline'>Tekuca godina </span>
                              ({report.tekuca_godina ?? '-'}) [{unit}]
                            </th>
                            <th className='text-white text-nowrap text-end pe-3 pe-sm-4'>
                              <span className='d-none d-sm-inline'>Prosla godina </span>
                              ({report.prosla_godina ?? '-'}) [{unit}]
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTab.months.map((month) => (
                            <tr key={month.mjesec}>
                              <td className='text-nowrap ps-3 ps-sm-4 fw-semibold text-gray-800'>
                                {MONTH_NAMES[month.mjesec - 1]}
                              </td>
                              <td className='text-nowrap text-end'>
                                {formatValue(month.tekuca_godina, unit)}
                              </td>
                              <td className='text-nowrap text-end pe-3 pe-sm-4'>
                                {formatValue(month.prosla_godina, unit)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className='fw-bolder bg-light-primary border-top border-2 border-gray-400'>
                            <td className='text-nowrap ps-3 ps-sm-4 text-gray-900'>Prosjek</td>
                            <td className='text-nowrap text-end'>
                              {formatAverage(selectedAverages.current, unit)}
                            </td>
                            <td className='text-nowrap text-end pe-3 pe-sm-4'>
                              {formatAverage(selectedAverages.previous, unit)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

const ProsjecnaDebljinaPanela: React.FC = () => {
  return (
    <KTCard className='mb-5 mb-xl-10'>
      <div className='card-header border-0 px-4 px-sm-9 py-4 py-sm-6'>
        <div className='card-title m-0 flex-column align-items-start'>
          <TitleComponent name='Prosjecna debljina panela' />
          <div className='text-muted fw-semibold fs-7 mt-2'>
            Prosječna debljina završenih panela po izolaciji i mjesecu, ponderisana proizvedenom
            količinom.
          </div>
        </div>
      </div>
      <KTCardBody className='border-top p-4 p-md-9'>
        <ProsjecnaDebljinaPanelaSadrzaj />
      </KTCardBody>
    </KTCard>
  )
}

export default ProsjecnaDebljinaPanela
